/* Gift selection is presentation only. A Shopify discount must authorize every free item. */
(() => {
  if (window.HBGiftPicker) return;
  const marker = '_hb_gift_campaign';

  function planGifts(state, campaign) {
    const items = state.items || [];
    const eligible = state.active
      ? items.filter((item) => item.qualifies && !item.giftCampaign)
        .reduce((total, item) => total + item.quantity, 0)
      : 0;
    // Count gifts already selected through the normal storefront, but never remove paid lines.
    const existing = items.filter((item) => !item.giftCampaign && item.giftCandidate
      && item.finalLinePrice === 0);
    let available = Math.max(0, eligible - existing.reduce((sum, item) => sum + item.quantity, 0));
    const selected = [...existing];
    const updates = {};
    items.filter((item) => item.giftCampaign).forEach((item) => {
      const valid = state.active && item.giftCampaign === campaign && item.giftCandidate;
      // A reduced entitlement can make a multi-quantity gift line partly paid.
      // Trim it first, then require Shopify to confirm the remaining units are free.
      const needsTrim = item.quantity > available;
      const keep = valid && (item.finalLinePrice === 0 || needsTrim)
        ? Math.min(available, item.quantity) : 0;
      if (keep !== item.quantity) updates[item.key] = keep;
      if (keep > 0 && item.finalLinePrice === 0) selected.push({ ...item, quantity: keep });
      available -= keep;
    });
    return { eligible, remaining: available, selected, updates };
  }

  function mergeCodes(codes, giftCode) {
    const values = (codes || []).map((item) => item.code).filter(Boolean);
    if (giftCode && !values.some((code) => code.toLowerCase() === giftCode.toLowerCase())) {
      values.push(giftCode);
    }
    return values.join(',');
  }

  class GiftPicker {
    constructor(config, dialog) {
      this.config = config;
      this.dialog = dialog;
      this.busy = false;
      this.failed = false;
      this.message = '';
      this.state = { active: false, items: [] };
      document.addEventListener('hb:gift-state', () => this.receive());
      document.addEventListener('click', (event) => this.handleClick(event));
      document.addEventListener('submit', (event) => this.handleCheckout(event), true);
      document.addEventListener('change', (event) => {
        if (event.target.matches('[data-gwp-variant]')) this.render();
      });
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right
          || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      });
      dialog.addEventListener('close', () => this.restoreFocus());
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.perform(() => this.reconcile());
      });
      this.receive();
    }

    receive() {
      const node = document.querySelector('[data-gwp-state]');
      if (!node) return false;
      try {
        this.state = JSON.parse(node.textContent);
        this.plan = planGifts(this.state, this.config.campaign);
        this.render();
        if (!this.busy && !this.failed && Object.keys(this.plan.updates).length) {
          this.perform(() => this.reconcile());
        }
        return true;
      } catch (error) {
        this.failed = true;
        this.showMessage(this.config.strings.verificationError, true);
        return false;
      }
    }

    summary() {
      if (!this.plan?.eligible) return this.config.strings.noEligibility;
      if (!this.plan.remaining) return this.config.strings.complete;
      return this.plan.remaining === 1 ? this.config.strings.oneRemaining
        : this.config.strings.manyRemaining.replace('[count]', this.plan.remaining);
    }

    render() {
      const plan = this.plan;
      if (!plan) return;
      document.querySelectorAll('[data-gwp-callout]').forEach((node) => {
        node.hidden = !this.state.active || plan.eligible === 0;
      });
      document.querySelectorAll('[data-gwp-summary], [data-gwp-remaining]').forEach((node) => {
        node.textContent = this.busy ? this.config.strings.loading : this.summary();
      });
      document.querySelectorAll('[data-gwp-open]').forEach((button) => {
        button.textContent = plan.remaining ? this.config.buttonLabel : this.config.strings.manage;
        button.disabled = this.busy;
      });
      this.dialog.querySelectorAll('[data-gwp-product]').forEach((card) => {
        const button = card.querySelector('[data-gwp-add]');
        button.disabled = this.busy || this.failed || !plan.remaining
          || !card.querySelector('[data-gwp-variant]').value;
        button.textContent = this.config.strings.add;
      });
      const list = this.dialog.querySelector('[data-gwp-selected-list]');
      const focusedKey = document.activeElement?.dataset?.gwpRemove;
      list.replaceChildren();
      plan.selected.forEach((item) => {
        const row = document.createElement('li');
        const description = document.createElement('div');
        const title = document.createElement('strong');
        title.textContent = item.title;
        const variant = document.createElement('span');
        variant.textContent = item.variant === 'Default Title' ? '' : item.variant;
        const quantity = document.createElement('span');
        quantity.textContent = this.config.strings.quantity.replace('[count]', item.quantity);
        description.append(title, variant, quantity);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'link';
        remove.dataset.gwpRemove = item.key;
        remove.textContent = this.config.strings.remove;
        remove.setAttribute('aria-label', this.config.strings.removeLabel.replace('[title]', item.title));
        remove.disabled = this.busy;
        row.append(description, remove);
        list.append(row);
        if (focusedKey === item.key && !this.busy) remove.focus();
      });
      this.dialog.querySelector('[data-gwp-selected]').hidden = !plan.selected.length;
      this.dialog.setAttribute('aria-busy', String(this.busy));
      document.querySelectorAll('[data-gwp-cart-message], [data-gwp-dialog-message]').forEach((node) => {
        node.textContent = this.message;
        node.hidden = !this.message;
        node.dataset.error = String(!!this.messageIsError);
      });
      document.querySelectorAll('[data-gwp-retry]').forEach((node) => {
        node.hidden = !this.failed;
        node.disabled = this.busy;
      });
    }

    showMessage(message, isError = false) {
      this.message = message;
      this.messageIsError = isError;
      this.render();
    }

    async perform(action) {
      if (this.busy) return;
      this.busy = true;
      this.failed = false;
      this.render();
      try {
        await action();
      } catch (error) {
        this.failed = true;
        this.showMessage(error.giftMessage || this.config.strings.verificationError, true);
      } finally {
        this.busy = false;
        this.receive();
        this.render();
      }
    }

    sections() {
      const drawer = document.querySelector('cart-drawer');
      const section = drawer?.closest('.shopify-section')?.id.replace('shopify-section-', '')
        || document.querySelector('cart-items')?.dataset.section;
      return [...new Set([section, 'cart-icon-bubble'].filter(Boolean))];
    }

    async request(path, body) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch((window.Shopify?.routes?.root || '/') + path, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
          signal: controller.signal
        });
        const data = await response.json();
        if (!response.ok) {
          const error = new Error('Gift cart request failed: ' + response.status);
          if (response.status === 422) error.giftMessage = this.config.strings.unavailable;
          throw error;
        }
        return data;
      } finally {
        clearTimeout(timeout);
      }
    }

    update(updates = {}, extra = {}) {
      return this.request('cart/update.js', {
        updates, ...extra, sections: this.sections(), sections_url: window.location.pathname
      });
    }

    async applyCart(data) {
      // Section failures do not imply that the preceding cart mutation failed.
      if (!data.sections || this.sections().some((id) => !data.sections[id])) {
        throw new Error('Cart section rendering failed');
      }
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        await drawer.renderContents(data, false);
      } else {
        this.sections().forEach((id) => {
          const target = document.getElementById('shopify-section-' + id);
          const parsed = new DOMParser().parseFromString(data.sections[id], 'text/html');
          const content = parsed.getElementById('shopify-section-' + id);
          if (target && content) target.innerHTML = content.innerHTML;
          else if (id === 'cart-icon-bubble') {
            const icon = document.getElementById(id);
            if (icon) icon.innerHTML = content?.innerHTML || data.sections[id];
          }
        });
      }
      if (!this.receive()) throw new Error('Cart eligibility state unavailable');
      document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
    }

    async reconcile(data) {
      let cart = data || await this.update();
      let changed = false;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        await this.applyCart(cart);
        const updates = this.plan.updates;
        if (!Object.keys(updates).length) {
          if (changed) this.showMessage(this.config.strings.adjusted);
          return { cart, changed };
        }
        changed = true;
        cart = await this.update(updates);
      }
      throw new Error('Gift quantities did not settle');
    }

    async add(variantId) {
      await this.perform(async () => {
        const { cart } = await this.reconcile();
        if (!this.plan.remaining) {
          this.showMessage(this.config.strings.limit, true);
          return;
        }
        const before = (cart.items || []).filter((item) => item.variant_id === variantId
          && item.properties?.[marker] === this.config.campaign)
          .reduce((sum, item) => sum + item.quantity, 0);
        try {
          await this.request('cart/add.js', {
            items: [{ id: variantId, quantity: 1, properties: { [marker]: this.config.campaign } }]
          });
          const extra = this.config.discountCode
            ? { discount: mergeCodes(cart.discount_codes, this.config.discountCode) } : {};
          const updated = await this.update({}, extra);
          const freeQuantity = (updated.items || []).filter((item) => item.variant_id === variantId
            && item.properties?.[marker] === this.config.campaign && item.final_line_price === 0)
            .reduce((sum, item) => sum + item.quantity, 0);
          await this.reconcile(updated);
          this.showMessage(freeQuantity > before ? this.config.strings.added
            : this.config.strings.notFree, freeQuantity <= before);
        } catch (error) {
          // Recover from a response lost after Shopify may already have accepted the add.
          await this.reconcile();
          throw error;
        }
      });
    }

    async remove(key) {
      await this.perform(async () => {
        await this.reconcile();
        if (!this.plan.selected.some((item) => item.key === key)) return;
        await this.reconcile(await this.update({ [key]: 0 }));
        this.showMessage('');
      });
    }

    open(opener) {
      this.opener = opener;
      this.drawerWasOpen = !!document.querySelector('cart-drawer[open]');
      if (this.drawerWasOpen) document.querySelector('cart-drawer').close();
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.dialog.showModal();
      this.perform(() => this.reconcile());
    }

    restoreFocus() {
      document.body.style.overflow = this.previousOverflow || '';
      const drawer = document.querySelector('cart-drawer');
      if (this.drawerWasOpen && drawer) {
        const opener = document.querySelector('[data-gwp-open]');
        drawer.open(document.getElementById('cart-icon'), opener && !opener.hidden ? opener : undefined);
      } else {
        // Cart section refreshes can replace the original opener while the dialog is open.
        const opener = this.opener?.isConnected ? this.opener : document.querySelector('[data-gwp-open]');
        if (opener && !opener.closest('[hidden]')) opener.focus();
      }
    }

    handleClick(event) {
      const target = event.target.closest('button, a');
      if (!target) return;
      if (target.matches('[data-gwp-open]')) this.open(target);
      if (target.matches('[data-gwp-close]')) this.dialog.close();
      if (target.matches('[data-gwp-retry]')) {
        this.perform(async () => { await this.reconcile(); this.showMessage(''); });
      }
      if (target.matches('[data-gwp-add]') && !target.disabled) {
        const value = target.closest('[data-gwp-product]').querySelector('[data-gwp-variant]').value;
        if (value) this.add(Number(value));
      }
      if (target.matches('[data-gwp-remove]') && !target.disabled) this.remove(target.dataset.gwpRemove);
    }

    handleCheckout(event) {
      if (this.allowCheckout || event.submitter?.name !== 'checkout'
        || !event.target.matches('form[action$="/cart"]')) return;
      if (!this.busy && !this.failed && !this.state.items.some((item) => item.giftCampaign)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.busy) return;
      const formId = event.target.id;
      this.perform(async () => {
        const result = await this.reconcile();
        if (result.changed) return; // Let the customer review a changed offer before proceeding.
        const form = document.getElementById(formId);
        const submitter = form?.querySelector('[name="checkout"]')
          || document.querySelector('[name="checkout"][form="' + formId + '"]');
        if (!form || !submitter) throw new Error('Checkout form unavailable');
        this.allowCheckout = true;
        try { form.requestSubmit(submitter); } finally { this.allowCheckout = false; }
      });
    }
  }

  window.HBGiftPicker = { planGifts, mergeCodes, GiftPicker };
  const config = document.getElementById('HB-Gift-Config');
  const dialog = document.getElementById('HB-Gift-Dialog');
  if (!config || !dialog) return;
  window.HBGiftPicker.instance = new GiftPicker(JSON.parse(config.textContent), dialog);
  if (!customElements.get('hb-gift-state')) {
    customElements.define('hb-gift-state', class extends HTMLElement {
      connectedCallback() {
        queueMicrotask(() => {
          if (this.isConnected) document.dispatchEvent(new CustomEvent('hb:gift-state'));
        });
      }
    });
  }
})();
